import { useEffect, useState } from "hono/jsx";
import { Input } from "@/common/components/Input.tsx";
import { useValidatedInput } from "@/common/hooks/useValidatedInput.ts";
import { CreateReservedRoomInput } from "@/features/auth/models.ts";

interface ReservedRoom {
  id: string;
  label: string;
  password?: string;
  isPublic?: boolean;
  geo?: { region?: string }; // Simple for now
}

export const ReservedRoomManager = (
  { initialReservedRooms = [] }: { initialReservedRooms?: ReservedRoom[] },
) => {
  const [loading, setLoading] = useState(false);
  const [reservedRooms, setReservedRooms] = useState<ReservedRoom[]>(
    initialReservedRooms,
  );

  // Form Hooks
  const roomId = useValidatedInput({
    schema: CreateReservedRoomInput.shape.roomId,
  });
  const label = useValidatedInput({
    schema: CreateReservedRoomInput.shape.label,
  });
  const password = useValidatedInput({
    schema: CreateReservedRoomInput.shape.password,
  });

  // Extra Settings
  const [isPublic, setIsPublic] = useState(false);
  const [geoRegion, setGeoRegion] = useState("");

  // Error State
  const [generalError, setGeneralError] = useState("");

  useEffect(() => {
    const fetchReserved = async () => {
      try {
        const res = await fetch("/api/rooms/reserved");
        if (res.ok) {
          const data = await res.json();
          setReservedRooms(data);
        }
      } catch (e) {
        console.error("Failed to fetch reserved rooms", e);
      }
    };

    if (initialReservedRooms.length === 0) {
      fetchReserved();
    }
  }, [initialReservedRooms.length]);

  const reserveRoom = async (e?: Event) => {
    if (e) e.preventDefault();

    // Validate all fields
    const isRoomValid = roomId.validate(true);
    const isLabelValid = label.validate(true);
    const isPasswordValid = password.validate(true);

    if (!isRoomValid || !isLabelValid || !isPasswordValid) return;

    setLoading(true);
    setGeneralError("");

    try {
      // Note: We need to update the API input model to accept these new fields if we want to send them
      // But looking at mod.ts, it uses `CreateReservedRoomInput`.
      // I probably need to update `CreateReservedRoomInput` in `src/features/auth/models.ts` first!
      // But for now, let's assume I'll update the model right after this or the API ignores extra fields?
      // Wait, Zod validator strips unknown fields. I MUST update the model first.
      // Proceeding assuming model update will follow.

      const res = await fetch("/api/rooms/reserved", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId: roomId.value,
          label: label.value,
          password: password.value,
          isPublic,
          geo: geoRegion ? { region: geoRegion } : undefined,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        // If it returns just IDs in `reserved` array (old behavior), we might need to re-fetch
        // But my new API returns `reserved: newReserved` which SHOULD be the full objects if I updated it correctly?
        // Let's check mod.ts...
        // `const newReserved = [...reservedIds, id];` -> It returns IDs!
        // The previous GET /reserved returned objects.
        // I should probably fix the POST response to return objects or just re-fetch.
        // For now, let's just re-fetch or optimistically update if we had the full object.

        // Re-fetch to be safe and simple
        const refresh = await fetch("/api/rooms/reserved");
        if (refresh.ok) {
          setReservedRooms(await refresh.json());
        }

        roomId.setValue("");
        label.setValue("");
        password.setValue("");
        setIsPublic(false);
        setGeoRegion("");
      } else {
        if (data.details && Array.isArray(data.details)) {
          // deno-lint-ignore no-explicit-any
          data.details.forEach((d: any) => {
            if (d.field === "roomId") roomId.setError(d.message);
            if (d.field === "label") label.setError(d.message);
            if (d.field === "password") password.setError(d.message);
          });
        } else {
          setGeneralError(data.error || "Failed to reserve");
        }
      }
    } catch (_e) {
      setGeneralError("Failed to reserve");
    } finally {
      setLoading(false);
    }
  };

  const unreserveRoom = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/rooms/reserved/` + id, {
        method: "DELETE",
      });
      // const data = await res.json();
      if (res.ok) {
        // Re-fetch
        const refresh = await fetch("/api/rooms/reserved");
        if (refresh.ok) {
          setReservedRooms(await refresh.json());
        }
      }
    } catch (e) {
      console.error("Failed to unreserve", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div class="space-y-6">
      <div class="card bg-base-100 shadow-xl border border-base-300">
        <div class="card-body">
          <h3 class="card-title text-xl font-black uppercase tracking-tight">
            Reserve a New Room ID
          </h3>
          <p class="text-sm opacity-70 italic">
            Reserved IDs are locked to your account.
          </p>

          <form onSubmit={reserveRoom} class="flex flex-col gap-2 mt-4">
            <div class="flex flex-col sm:flex-row gap-2">
              <Input
                {...roomId.bind}
                placeholder="ROOM CODE"
                containerClass="grow"
                class="font-mono font-black uppercase"
                autoFocus
              />
              <Input
                {...label.bind}
                placeholder="LABEL"
                containerClass="grow"
                class="font-bold uppercase"
              />
            </div>

            <div class="flex flex-col sm:flex-row gap-2">
              <Input
                {...password.bind}
                type="password"
                placeholder="PASSWORD (OPTIONAL)"
                containerClass="grow"
                class="font-bold uppercase"
              />

              {/* Geo Region (Simple Text for now) */}
              <div class="form-control grow">
                <input
                  type="text"
                  placeholder="GEO REGION (e.g. Africa)"
                  class="input input-bordered font-bold uppercase w-full"
                  value={geoRegion}
                  onInput={(e) =>
                    setGeoRegion((e.target as HTMLInputElement).value)}
                />
              </div>
            </div>

            {/* Toggles */}
            <div class="form-control w-fit">
              <label class="label cursor-pointer gap-2">
                <span class="label-text font-bold uppercase">Public Room</span>
                <input
                  type="checkbox"
                  class="toggle toggle-secondary"
                  checked={isPublic}
                  onChange={(e) =>
                    setIsPublic((e.target as HTMLInputElement).checked)}
                />
              </label>
            </div>

            {generalError && (
              <div class="text-error font-bold text-sm mt-2">
                {generalError}
              </div>
            )}

            <div class="card-actions justify-end mt-4">
              <button
                type="submit"
                class={`btn btn-secondary font-black uppercase ${
                  loading ? "loading" : ""
                }`}
                disabled={loading || reservedRooms.length >= 5}
              >
                {reservedRooms.length >= 5
                  ? "Limit Reached (5/5)"
                  : "Reserve ID"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {reservedRooms.length > 0 && (
        <div class="space-y-4">
          <h3 class="text-xs uppercase font-black opacity-30 tracking-widest text-center">
            Your Active Reservations
          </h3>
          <div class="grid gap-2">
            {reservedRooms.map((r) => (
              <div
                key={r.id}
                class="flex items-center justify-between p-4 bg-base-100 rounded-xl shadow-md border border-base-300 border-l-4 border-l-secondary transform hover:scale-[1.01] transition-transform"
              >
                <div class="flex flex-col items-start">
                  <div class="flex items-center gap-2">
                    <span class="font-black font-mono text-lg">{r.id}</span>
                    <span class="badge badge-secondary badge-xs opacity-50 uppercase font-black">
                      {r.label || "No Label"}
                    </span>
                    {r.password && (
                      <span class="badge badge-error badge-xs">🔒</span>
                    )}
                    {r.isPublic && (
                      <span class="badge badge-info badge-xs">Public</span>
                    )}
                    {/* Access safe nested props if mixed types exist */}
                    {(r.geo as any)?.region && (
                      <span class="badge badge-accent badge-xs">
                        {(r.geo as any).region}
                      </span>
                    )}
                  </div>
                </div>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-ghost text-error font-black uppercase text-xs"
                    onClick={() => unreserveRoom(r.id)}
                  >
                    Release
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
